import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  FormControlLabel,
  Checkbox,
  useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PieChart } from '@mui/x-charts/PieChart';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { getAuthHeaders } from '../../../utils/authUtils';
import { createApiUrl } from '../../../utils/apiConfig';
import { useSnackbar } from '../../CentralSnackbar';
import { useFormatConfig, formatDateSync } from '../../../utils/dateTimeFormat';

const Modalities = forwardRef(({ currentUser }, ref) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const { isLoaded: isFormatConfigLoaded } = useFormatConfig();
  const theme = useTheme();
  
  // Refs für die Donut Charts
  const chartsContainerRef = useRef(null);
  
  // State
  const [modalitiesData, setModalitiesData] = useState(null);
  const [modalitiesLoading, setModalitiesLoading] = useState(false);
  const [timeFilter, setTimeFilter] = useState('month'); // Default: Monat
  const [startPeriod, setStartPeriod] = useState('');
  const [endPeriod, setEndPeriod] = useState('');
  const [selectedModalities, setSelectedModalities] = useState([]); // Mehrfachauswahl
  
  // Donut-Toggles
  const [showMyReports, setShowMyReports] = useState(true);
  const [showForeignSignedReports, setShowForeignSignedReports] = useState(true);
  const [showIndicationApprovals, setShowIndicationApprovals] = useState(true);
  const [showReleasedReports, setShowReleasedReports] = useState(true);

  // Helper: Get current period based on timeFilter
  const getCurrentPeriod = (filterType) => {
    const now = new Date();
    switch (filterType) {
      case 'date':
        return now.toISOString().split('T')[0];
      case 'week': {
        const year = now.getFullYear();
        const oneJan = new Date(year, 0, 1);
        const numberOfDays = Math.floor((now - oneJan) / (24 * 60 * 60 * 1000));
        const weekNumber = Math.ceil((numberOfDays + oneJan.getDay() + 1) / 7);
        return `${year}-W${String(weekNumber).padStart(2, '0')}`;
      }
      case 'month':
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      case 'quarter': {
        const quarter = Math.ceil((now.getMonth() + 1) / 3);
        return `${now.getFullYear()}-Q${quarter}`;
      }
      case 'year':
        return `${now.getFullYear()}`;
      default:
        return '';
    }
  };

  // Helper: Get next/previous period
  const getAdjacentPeriod = (period, filterType, direction) => {
    if (!period) return period;
    
    switch (filterType) {
      case 'date': {
        const date = new Date(period);
        date.setDate(date.getDate() + direction);
        return date.toISOString().split('T')[0];
      }
      case 'week': {
        const [year, week] = period.split('-W').map(Number);
        let newWeek = week + direction;
        let newYear = year;
        
        if (newWeek < 1) {
          newYear--;
          newWeek = 52;
        } else if (newWeek > 52) {
          newYear++;
          newWeek = 1;
        }
        return `${newYear}-W${String(newWeek).padStart(2, '0')}`;
      }
      case 'month': {
        const [year, month] = period.split('-').map(Number);
        let newMonth = month + direction;
        let newYear = year;
        
        if (newMonth < 1) {
          newYear--;
          newMonth = 12;
        } else if (newMonth > 12) {
          newYear++;
          newMonth = 1;
        }
        return `${newYear}-${String(newMonth).padStart(2, '0')}`;
      }
      case 'quarter': {
        const [year, quarter] = period.split('-Q').map(Number);
        let newQuarter = quarter + direction;
        let newYear = year;
        
        if (newQuarter < 1) {
          newYear--;
          newQuarter = 4;
        } else if (newQuarter > 4) {
          newYear++;
          newQuarter = 1;
        }
        return `${newYear}-Q${newQuarter}`;
      }
      case 'year': {
        const year = parseInt(period);
        return `${year + direction}`;
      }
      default:
        return period;
    }
  };

  // Expose download functions to parent component via ref
  useImperativeHandle(ref, () => ({
    handlePdfDownload,
    handleCsvDownload
  }));

  // Set default period on mount
  useEffect(() => {
    const currentPeriod = getCurrentPeriod(timeFilter);
    setStartPeriod(currentPeriod);
    setEndPeriod(currentPeriod);
  }, []);

  // Wheel event for period scrolling (Shift + Scroll)
  useEffect(() => {
    const handleWheel = (event) => {
      if (event.shiftKey && (startPeriod || endPeriod)) {
        event.preventDefault();
        
        const direction = event.deltaY > 0 ? -1 : 1;
        
        setStartPeriod(prev => getAdjacentPeriod(prev, timeFilter, direction));
        setEndPeriod(prev => getAdjacentPeriod(prev, timeFilter, direction));
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [timeFilter, startPeriod, endPeriod]);

  // Load Modalities data on mount
  useEffect(() => {
    loadModalitiesData();
  }, []);

  const loadModalitiesData = async () => {
    setModalitiesLoading(true);
    try {
      const response = await fetch(createApiUrl('statistics/personal/modalities'), {
        headers: { ...getAuthHeaders() }
      });
      
      if (response.ok) {
        const data = await response.json();
        setModalitiesData(data.modalities);
        
        // Automatisch alle Modalitäten auswählen beim ersten Laden
        if (data.modalities) {
          const allModalities = Object.keys(data.modalities);
          setSelectedModalities(allModalities);
        }
      } else {
        showError(t('statistics.personal.modalitaeten.loadError', 'Fehler beim Laden der Modalitäten'), 'ERR-STATS-MOD-001');
      }
    } catch (error) {
      console.error('Error loading Modalities data:', error);
      showError(t('statistics.personal.modalitaeten.loadError', 'Fehler beim Laden der Modalitäten'), 'ERR-STATS-MOD-002');
    } finally {
      setModalitiesLoading(false);
    }
  };

  const handleTimeFilterChange = (event) => {
    const newFilterType = event.target.value;
    setTimeFilter(newFilterType);
    const currentPeriod = getCurrentPeriod(newFilterType);
    setStartPeriod(currentPeriod);
    setEndPeriod(currentPeriod);
  };

  const handleStartPeriodChange = (event) => {
    setStartPeriod(event.target.value);
  };

  const handleEndPeriodChange = (event) => {
    setEndPeriod(event.target.value);
  };

  const handleModalitiesChange = (event) => {
    const value = event.target.value;
    setSelectedModalities(typeof value === 'string' ? value.split(',') : value);
  };

  // CSV Export Function
  const handleCsvDownload = () => {
    if (!modalitiesData || selectedModalities.length === 0) return;

    // Build CSV - nur aktive Serien exportieren
    let csv = '\uFEFF'; // BOM for Excel UTF-8
    csv += '"' + t('statistics.personal.modalitaeten.selectModalities', 'Modalitäten') + '";';
    if (showIndicationApprovals) csv += '"' + t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen') + '";';
    if (showMyReports) csv += '"' + t('statistics.personal.pdf.composedReports', 'Verfasste Befunde') + '";';
    if (showForeignSignedReports) csv += '"' + t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde') + '";';
    if (showReleasedReports) csv += '"' + t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde') + '";';
    csv = csv.slice(0, -1) + '\n'; // Entferne das letzte Semikolon

    const myReportsResult = aggregateDataForCategory('my_reports');
    const foreignSignedResult = aggregateDataForCategory('foreign_signed_reports');
    const indicationsResult = aggregateDataForCategory('indication_approvals');
    const releasedResult = aggregateDataForCategory('released_reports');

    selectedModalities.forEach(modality => {
      csv += '"' + modality + '";';
      
      if (showIndicationApprovals) {
        const count = indicationsResult.data.find(d => d.label === modality)?.value || 0;
        csv += count + ';';
      }
      if (showMyReports) {
        const count = myReportsResult.data.find(d => d.label === modality)?.value || 0;
        csv += count + ';';
      }
      if (showForeignSignedReports) {
        const count = foreignSignedResult.data.find(d => d.label === modality)?.value || 0;
        csv += count + ';';
      }
      if (showReleasedReports) {
        const count = releasedResult.data.find(d => d.label === modality)?.value || 0;
        csv += count + ';';
      }
      
      csv = csv.slice(0, -1) + '\n'; // Entferne das letzte Semikolon
    });

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `modalitaeten_statistik_${currentUser?.id}_${new Date().getTime()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // PDF Export Function
  const handlePdfDownload = async () => {
    if (!modalitiesData || selectedModalities.length === 0 || !chartsContainerRef.current) return;

    try {
      // Farben dynamisch aus CSS-Variablen auslesen (für Modalitäten-Legende)
      const rootStyles = getComputedStyle(document.documentElement);
      const getChartColor = (index) => {
        const colorVar = `--rd-chart-${(index % 10) + 1}`;
        return rootStyles.getPropertyValue(colorVar).trim();
      };

      // Screenshot der Charts erstellen
      const chartsElement = chartsContainerRef.current;
      const chartsCanvas = await html2canvas(chartsElement, {
        scale: 2,
        backgroundColor: 'var(--rd-bg-paper)'
      });
      const chartsImage = chartsCanvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPosition = 20;

      // Berechne tatsächlichen Zeitraum basierend auf timeFilter und startPeriod
      const calculatePeriodRange = () => {
        if (!startPeriod) {
          // Finde min/max aus allen Daten
          const allPeriods = new Set();
          Object.keys(modalitiesData).forEach(modality => {
            const data = modalitiesData[modality].my_reports;
            const periodData = data[`by_${timeFilter}`] || [];
            periodData.forEach(d => allPeriods.add(d.period));
          });
          const sortedPeriods = Array.from(allPeriods).sort();
          return {
            start: sortedPeriods[0] || '',
            end: sortedPeriods[sortedPeriods.length - 1] || ''
          };
        }
        
        // Einzelner Zeitraum gewählt
        return { start: startPeriod, end: startPeriod };
      };

      const periodRange = calculatePeriodRange();
      const actualStartPeriod = periodRange.start;
      const actualEndPeriod = periodRange.end;

      const addFooter = () => {
        const footerY = pageHeight - 10;
        pdf.setFontSize(7);
        pdf.setTextColor(100, 100, 100);
        const footerText = `Modalitätenbericht, ${currentUser?.title || ''} ${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() +
          `, Zeitraum ${formatPeriod(actualStartPeriod)} - ${formatPeriod(actualEndPeriod)}`;
        pdf.text(footerText, 20, footerY);
        pdf.setTextColor(0, 0, 0);
      };

      pdf.setFontSize(18);
      pdf.text(t('statistics.personal.pdf.title', 'Befund-Report'), 20, yPosition);
      yPosition += 15;

      pdf.setFontSize(12);
      const userName = `${currentUser?.title || ''} ${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim();
      pdf.text(`${t('statistics.personal.pdf.reportingPhysician', 'Befunder')}: ${userName}`, 20, yPosition);
      yPosition += 7;

      const periodText = `${t('statistics.personal.pdf.period', 'Zeitraum')}: ${formatPeriod(actualStartPeriod)}${actualStartPeriod !== actualEndPeriod ? ' - ' + formatPeriod(actualEndPeriod) : ''}`;
      pdf.text(periodText, 20, yPosition);
      yPosition += 15;

      // Charts einfügen
      const chartsWidth = 170;
      const chartsHeight = (chartsCanvas.height * chartsWidth) / chartsCanvas.width;
      pdf.addImage(chartsImage, 'PNG', 20, yPosition, chartsWidth, chartsHeight);
      yPosition += chartsHeight + 10;

      // Legende (Modalitäten) - horizontal mit festen Spalten
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.text(t('statistics.personal.pdf.legend', 'Legende') + ':', 20, yPosition);
      pdf.setFont('helvetica', 'normal');
      yPosition += 5; // Abstand nach Überschrift
      
      // Legende horizontal anordnen mit festen Spalten-Positionen
      const availableModalities = Object.keys(modalitiesData);
      const columnWidth = 45; // Feste Breite pro Spalte
      const columnsPerRow = 3; // Anzahl Spalten pro Zeile
      const startX = 20; // Start X-Position
      
      selectedModalities.forEach((modality, index) => {
        const modalityIndex = availableModalities.indexOf(modality);
        const color = getChartColor(modalityIndex);
        
        const column = index % columnsPerRow;
        const row = Math.floor(index / columnsPerRow);
        const xPosition = startX + (column * columnWidth);
        const currentY = yPosition + (row * 4);
        
        pdf.setFillColor(color);
        pdf.rect(xPosition, currentY - 1.5, 2, 2, 'F');
        pdf.setFontSize(6);
        pdf.text(modality, xPosition + 3, currentY);
      });

      // Berechne finale Y-Position nach allen Legendenzeilen
      const totalRows = Math.ceil(selectedModalities.length / columnsPerRow);
      yPosition += (totalRows * 4) + 4;

      // Tabelle
      const tableHeaders = [t('statistics.personal.modalitaeten.selectModalities', 'Modalitäten')];
      if (showIndicationApprovals) tableHeaders.push(t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen'));
      if (showMyReports) tableHeaders.push(t('statistics.personal.pdf.composedReports', 'Verfasste Befunde'));
      if (showForeignSignedReports) tableHeaders.push(t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde'));
      if (showReleasedReports) tableHeaders.push(t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde'));

      const myReportsResult = aggregateDataForCategory('my_reports');
      const foreignSignedResult = aggregateDataForCategory('foreign_signed_reports');
      const indicationsResult = aggregateDataForCategory('indication_approvals');
      const releasedResult = aggregateDataForCategory('released_reports');

      const tableData = selectedModalities.map(modality => {
        const rowData = [modality];
        if (showIndicationApprovals) rowData.push(indicationsResult.data.find(d => d.label === modality)?.value || 0);
        if (showMyReports) rowData.push(myReportsResult.data.find(d => d.label === modality)?.value || 0);
        if (showForeignSignedReports) rowData.push(foreignSignedResult.data.find(d => d.label === modality)?.value || 0);
        if (showReleasedReports) rowData.push(releasedResult.data.find(d => d.label === modality)?.value || 0);
        return rowData;
      });

      pdf.autoTable({
        head: [tableHeaders],
        body: tableData,
        startY: yPosition,
        margin: { left: 20, right: 20, bottom: 20 },
        styles: {
          fontSize: 8,
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        },
        bodyStyles: {
          fillColor: [255, 255, 255],
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255]
        },
        didDrawPage: (data) => {
          addFooter();
        }
      });

      yPosition = pdf.lastAutoTable.finalY + 60;

      if (yPosition + 30 > pageHeight - 20) {
        pdf.addPage();
        yPosition = 140;
      }

      // Unterschrift
      pdf.setFontSize(10);
      pdf.line(20, yPosition, 80, yPosition);
      yPosition += 5;
      pdf.text(t('statistics.personal.pdf.signatureStamp', 'Unterschrift und Stempel'), 20, yPosition);
      yPosition += 15;

      // Elektronisch erzeugt
      pdf.setFontSize(7);
      pdf.setTextColor(100, 100, 100);
      const now = new Date();
      const dateStr = formatDateSync(now.toISOString().split('T')[0]);
      const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const electronicallyGenerated = t('statistics.personal.pdf.electronicallyGenerated',
        'Der Bericht wurde elektronisch erzeugt durch {{name}} am {{date}} um {{time}}.',
        {
          name: userName,
          date: dateStr,
          time: timeStr
        }
      );
      pdf.text(electronicallyGenerated, 20, yPosition);

      addFooter();

      pdf.save('modalities_report.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      showError(t('statistics.personal.modalitaeten.loadError', 'Fehler beim Erstellen der PDF'));
    }
  };

  // Hilfsfunktion: Formatiere Periode
  const formatPeriod = (period) => {
    if (!period) return period;
    
    try {
      switch (timeFilter) {
        case 'date':
          if (!isFormatConfigLoaded) return period;
          return formatDateSync(period);
        
        case 'week':
          const weekMatch = period.match(/(\d{4})-W(\d{2})/);
          if (weekMatch) {
            return `KW${weekMatch[2]} - ${weekMatch[1]}`;
          }
          return period;
        
        case 'month':
          const monthMatch = period.match(/(\d{4})-(\d{2})/);
          if (monthMatch) {
            return `${monthMatch[2]}.${monthMatch[1]}`;
          }
          return period;
        
        case 'quarter':
          const quarterMatch = period.match(/(\d{4})-Q(\d)/);
          if (quarterMatch) {
            return `Q${quarterMatch[2]} - ${quarterMatch[1]}`;
          }
          return period;
        
        case 'year':
          return period;
        
        default:
          return period;
      }
    } catch (error) {
      console.error('Error formatting period:', error);
      return period;
    }
  };

  // Hilfsfunktion: Hole alle verfügbaren Perioden
  const getAvailablePeriods = () => {
    if (!modalitiesData) return [];
    
    const periods = new Set();
    
    Object.keys(modalitiesData).forEach(modality => {
      const modalityData = modalitiesData[modality];
      
      switch (timeFilter) {
        case 'date':
          modalityData.my_reports.by_date.forEach(d => periods.add(d.period));
          break;
        case 'week':
          modalityData.my_reports.by_week.forEach(d => periods.add(d.period));
          break;
        case 'month':
          modalityData.my_reports.by_month.forEach(d => periods.add(d.period));
          break;
        case 'quarter':
          modalityData.my_reports.by_quarter.forEach(d => periods.add(d.period));
          break;
        case 'year':
          modalityData.my_reports.by_year.forEach(d => periods.add(d.period));
          break;
        default:
          break;
      }
    });
    
    return Array.from(periods).sort().reverse();
  };

  // Aggregiere Daten für eine Kategorie
  const aggregateDataForCategory = (categoryKey) => {
    if (!modalitiesData || selectedModalities.length === 0) return { data: [], total: 0 };
    
    const result = {};
    let total = 0;
    
    selectedModalities.forEach(modality => {
      if (!modalitiesData[modality]) return;
      
      const modalityDataForCategory = modalitiesData[modality][categoryKey];
      if (!modalityDataForCategory) return;
      
      let dataArray = [];
      switch (timeFilter) {
        case 'date':
          dataArray = modalityDataForCategory.by_date || [];
          break;
        case 'week':
          dataArray = modalityDataForCategory.by_week || [];
          break;
        case 'month':
          dataArray = modalityDataForCategory.by_month || [];
          break;
        case 'quarter':
          dataArray = modalityDataForCategory.by_quarter || [];
          break;
        case 'year':
          dataArray = modalityDataForCategory.by_year || [];
          break;
        default:
          dataArray = modalityDataForCategory.by_month || [];
      }
      
      // Filter nach Zeitraum
      dataArray.forEach(item => {
        const period = item.period;
        
        // Prüfe ob Periode im gewählten Zeitraum liegt
        if (startPeriod && period < startPeriod) return;
        if (endPeriod && period > endPeriod) return;
        
        // Aggregiere nach Modalität
        if (!result[modality]) {
          result[modality] = 0;
        }
        result[modality] += item.count;
        total += item.count;
      });
    });
    
    const data = Object.entries(result).map(([modality, count]) => ({
      label: modality,
      value: count
    }));
    
    return { data, total };
  };

  // Farben aus CSS für Modalitäten (var(--rd-chart-1) bis var(--rd-chart-10))
  const getModalityColor = (index) => {
    const colorVars = [
      'var(--rd-chart-1)',
      'var(--rd-chart-2)',
      'var(--rd-chart-3)',
      'var(--rd-chart-4)',
      'var(--rd-chart-5)',
      'var(--rd-chart-6)',
      'var(--rd-chart-7)',
      'var(--rd-chart-8)',
      'var(--rd-chart-9)',
      'var(--rd-chart-10)'
    ];
    
    // Konvertiere CSS-Variable zu tatsächlichem Farbwert
    const colorVar = colorVars[index % colorVars.length];
    const tempDiv = document.createElement('div');
    tempDiv.style.color = colorVar;
    document.body.appendChild(tempDiv);
    const computedColor = window.getComputedStyle(tempDiv).color;
    document.body.removeChild(tempDiv);
    
    return computedColor || colorVar;
  };

  if (modalitiesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!modalitiesData) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          {t('statistics.personal.modalitaeten.noData', 'Keine Daten verfügbar')}
        </Typography>
      </Box>
    );
  }

  const availablePeriods = getAvailablePeriods();
  const availableModalities = Object.keys(modalitiesData);
  
  const myReportsResult = aggregateDataForCategory('my_reports');
  const foreignSignedResult = aggregateDataForCategory('foreign_signed_reports');
  const indicationsResult = aggregateDataForCategory('indication_approvals');
  const releasedResult = aggregateDataForCategory('released_reports');

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('statistics.personal.categories.modalitaeten', 'Modalitäten')}
      </Typography>

      {/* Filter */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl sx={{ width: 120 }}>
          <InputLabel>{t('statistics.personal.pdf.interval', 'Intervall')}</InputLabel>
          <Select
            value={timeFilter}
            onChange={handleTimeFilterChange}
            label={t('statistics.personal.pdf.interval', 'Intervall')}
          >
            <MenuItem value="date">{t('statistics.personal.fallzahlen.day', 'Tag')}</MenuItem>
            <MenuItem value="week">{t('statistics.personal.fallzahlen.week', 'Woche')}</MenuItem>
            <MenuItem value="month">{t('statistics.personal.fallzahlen.month', 'Monat')}</MenuItem>
            <MenuItem value="quarter">{t('statistics.personal.fallzahlen.quarter', 'Quartal')}</MenuItem>
            <MenuItem value="year">{t('statistics.personal.fallzahlen.year', 'Jahr')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ width: 120 }}>
          <InputLabel>{t('statistics.personal.modalitaeten.period', 'Zeitraum')}</InputLabel>
          <Select
            value={startPeriod}
            onChange={handleStartPeriodChange}
            label={t('statistics.personal.modalitaeten.period', 'Zeitraum')}
          >
            <MenuItem value="">{t('statistics.personal.fallzahlen.all', 'Alle')}</MenuItem>
            {availablePeriods.map((period) => (
              <MenuItem key={period} value={period}>
                {formatPeriod(period)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 300, flex: 1 }}>
          <InputLabel>{t('statistics.personal.modalitaeten.selectModalities', 'Modalitäten')}</InputLabel>
          <Select
            multiple
            value={selectedModalities}
            onChange={handleModalitiesChange}
            input={<OutlinedInput label={t('statistics.personal.modalitaeten.selectModalities', 'Modalitäten')} />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const modalityIndex = availableModalities.indexOf(value);
                  const chipColor = getModalityColor(modalityIndex);
                  return (
                    <Chip 
                      key={value} 
                      label={value} 
                      size="small"
                      onMouseDown={(event) => {
                        event.stopPropagation();
                      }}
                      onDelete={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        const newSelected = selectedModalities.filter(m => m !== value);
                        setSelectedModalities(newSelected);
                      }}
                      sx={{
                        backgroundColor: chipColor,
                        color: 'white',
                        '& .MuiChip-deleteIcon': {
                          color: 'rgba(255, 255, 255, 0.7)',
                          '&:hover': {
                            color: 'white'
                          }
                        }
                      }}
                    />
                  );
                })}
              </Box>
            )}
          >
            {availableModalities.map((modality) => (
              <MenuItem key={modality} value={modality}>
                {modality}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Donut Charts */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box ref={chartsContainerRef} sx={{ display: 'flex', gap: 2, justifyContent: 'space-around', flexWrap: 'wrap' }}>
          {/* Indikationsprüfungen */}
          {showIndicationApprovals && (
            <Box sx={{ textAlign: 'center', minWidth: 250 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen')}
              </Typography>
              {indicationsResult.data.length > 0 ? (
                <PieChart
                  series={[
                    {
                      data: indicationsResult.data.map((item, index) => ({
                        id: index,
                        value: item.value,
                        label: item.label,
                        color: getModalityColor(availableModalities.indexOf(item.label))
                      })),
                      innerRadius: 50,
                      outerRadius: 100,
                      paddingAngle: 2,
                      cornerRadius: 5,
                      startAngle: 0,
                      endAngle: 360,
                      cx: 125,
                      cy: 125,
                    }
                  ]}
                  width={250}
                  height={250}
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                  sx={{
                    '& .MuiPieArc-root': {
                      cursor: 'pointer',
                      stroke: 'none'
                    },
                    '& .MuiChartsLegend-root': {
                      display: 'none !important'
                    }
                  }}
                >
                  <text x={125} y={125} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 24, fontWeight: 'bold', fill: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.text.secondary }}>
                    {indicationsResult.total}
                  </text>
                </PieChart>
              ) : (
                <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('statistics.personal.modalitaeten.noData', 'Keine Daten')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Meine Befunde */}
          {showMyReports && (
            <Box sx={{ textAlign: 'center', minWidth: 250 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('statistics.personal.fallzahlen.myReports', 'Meine Befunde')}
              </Typography>
              {myReportsResult.data.length > 0 ? (
                <PieChart
                  series={[
                    {
                      data: myReportsResult.data.map((item, index) => ({
                        id: index,
                        value: item.value,
                        label: item.label,
                        color: getModalityColor(availableModalities.indexOf(item.label))
                      })),
                      innerRadius: 50,
                      outerRadius: 100,
                      paddingAngle: 2,
                      cornerRadius: 5,
                      startAngle: 0,
                      endAngle: 360,
                      cx: 125,
                      cy: 125,
                    }
                  ]}
                  width={250}
                  height={250}
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                  sx={{
                    '& .MuiPieArc-root': {
                      cursor: 'pointer',
                      stroke: 'none'
                    },
                    '& .MuiChartsLegend-root': {
                      display: 'none !important'
                    }
                  }}
                >
                  <text x={125} y={125} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 24, fontWeight: 'bold', fill: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.text.secondary }}>
                    {myReportsResult.total}
                  </text>
                </PieChart>
              ) : (
                <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('statistics.personal.modalitaeten.noData', 'Keine Daten')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* (Fremd-)signierte Befunde */}
          {showForeignSignedReports && (
            <Box sx={{ textAlign: 'center', minWidth: 250 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde')}
              </Typography>
              {foreignSignedResult.data.length > 0 ? (
                <PieChart
                  series={[
                    {
                      data: foreignSignedResult.data.map((item, index) => ({
                        id: index,
                        value: item.value,
                        label: item.label,
                        color: getModalityColor(availableModalities.indexOf(item.label))
                      })),
                      innerRadius: 50,
                      outerRadius: 100,
                      paddingAngle: 2,
                      cornerRadius: 5,
                      startAngle: 0,
                      endAngle: 360,
                      cx: 125,
                      cy: 125,
                    }
                  ]}
                  width={250}
                  height={250}
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                  sx={{
                    '& .MuiPieArc-root': {
                      cursor: 'pointer',
                      stroke: 'none'
                    },
                    '& .MuiChartsLegend-root': {
                      display: 'none !important'
                    }
                  }}
                >
                  <text x={125} y={125} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 24, fontWeight: 'bold', fill: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.text.secondary }}>
                    {foreignSignedResult.total}
                  </text>
                </PieChart>
              ) : (
                <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('statistics.personal.modalitaeten.noData', 'Keine Daten')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Freigegebene Befunde */}
          {showReleasedReports && (
            <Box sx={{ textAlign: 'center', minWidth: 250 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde')}
              </Typography>
              {releasedResult.data.length > 0 ? (
                <PieChart
                  series={[
                    {
                      data: releasedResult.data.map((item, index) => ({
                        id: index,
                        value: item.value,
                        label: item.label,
                        color: getModalityColor(availableModalities.indexOf(item.label))
                      })),
                      innerRadius: 50,
                      outerRadius: 100,
                      paddingAngle: 2,
                      cornerRadius: 5,
                      startAngle: 0,
                      endAngle: 360,
                      cx: 125,
                      cy: 125,
                    }
                  ]}
                  width={250}
                  height={250}
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                  sx={{
                    '& .MuiPieArc-root': {
                      cursor: 'pointer',
                      stroke: 'none'
                    },
                    '& .MuiChartsLegend-root': {
                      display: 'none !important'
                    }
                  }}
                >
                  <text x={125} y={125} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 24, fontWeight: 'bold', fill: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.text.secondary }}>
                    {releasedResult.total}
                  </text>
                </PieChart>
              ) : (
                <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('statistics.personal.modalitaeten.noData', 'Keine Daten')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Checkboxen zum Ein-/Ausblenden der Donuts */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', mt: 2, borderTop: 1, borderColor: 'divider', pt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={showIndicationApprovals} 
                onChange={(e) => setShowIndicationApprovals(e.target.checked)}
                sx={{ color: 'var(--rd-chart-1)', '&.Mui-checked': { color: 'var(--rd-chart-1)' } }}
              />
            }
            label={t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen')}
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={showMyReports} 
                onChange={(e) => setShowMyReports(e.target.checked)}
                sx={{ color: 'var(--rd-chart-2)', '&.Mui-checked': { color: 'var(--rd-chart-2)' } }}
              />
            }
            label={t('statistics.personal.fallzahlen.myReports', 'Meine Befunde')}
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={showForeignSignedReports} 
                onChange={(e) => setShowForeignSignedReports(e.target.checked)}
                sx={{ color: 'var(--rd-chart-3)', '&.Mui-checked': { color: 'var(--rd-chart-3)' } }}
              />
            }
            label={t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde')}
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={showReleasedReports} 
                onChange={(e) => setShowReleasedReports(e.target.checked)}
                sx={{ color: 'var(--rd-chart-4)', '&.Mui-checked': { color: 'var(--rd-chart-4)' } }}
              />
            }
            label={t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde')}
          />
        </Box>
      </Paper>
    </Box>
  );
});

Modalities.displayName = 'Modalities';

export default Modalities;